import axios, { type AxiosInstance, type AxiosResponse } from 'axios';

import { API_URL } from './constants';

export { REFRESH_TOKEN_URL } from './constants';

export const apiClient: AxiosInstance = axios.create({ baseURL: API_URL });

export const apis = {
  login: (body: { email: string; password: string }): Promise<AxiosResponse> =>
    apiClient.post('/auth/login', body),

  checkAccessible: (
    friendlyUrl: string,
    module: string
  ): Promise<AxiosResponse<{ accessible?: boolean }>> =>
    apiClient.get(
      `/ehs-visitor-management/${friendlyUrl}/${module}/accessible`
    ),

  getConfiguration: (
    friendlyUrl: string,
    module: string
  ): Promise<AxiosResponse> =>
    apiClient.get(
      `/ehs-visitor-management/${friendlyUrl}/${module}/configuration`
    ),

  createVisitor: (
    friendlyUrl: string,
    module: string,
    body: unknown
  ): Promise<AxiosResponse> =>
    apiClient.post(
      `/ehs-visitor-management/${friendlyUrl}/${module}/store`,
      body
    ),

  elearningWpRemote: {
    login: (
      friendlyUrl: string,
      body: { token: string }
    ): Promise<AxiosResponse> =>
      apiClient.post(
        `/ehs-visitor-management/${friendlyUrl}/elearning-wp-remote/login`,
        body
      ),

    generateOtp: (friendlyUrl: string): Promise<AxiosResponse> =>
      apiClient.post(
        `/ehs-visitor-management/${friendlyUrl}/elearning-wp-remote/generate-otp`
      ),

    verifyOtp: (
      friendlyUrl: string,
      body: { otp: string }
    ): Promise<AxiosResponse> =>
      apiClient.post(
        `/ehs-visitor-management/${friendlyUrl}/elearning-wp-remote/otp`,
        body
      ),

    getPersonInfo: (friendlyUrl: string): Promise<AxiosResponse> =>
      apiClient.get(
        `/ehs-visitor-management/${friendlyUrl}/elearning-wp-remote`
      ),

    updatePerson: (
      friendlyUrl: string,
      contactId: string,
      body: unknown
    ): Promise<AxiosResponse> =>
      apiClient.post(
        `/ehs-visitor-management/${friendlyUrl}/elearning-wp-remote/${contactId}/update`,
        body
      ),

    dataProtectionDecline: (
      friendlyUrl: string,
      contactId: string,
      body: unknown
    ): Promise<AxiosResponse> =>
      apiClient.post(
        `/ehs-visitor-management/${friendlyUrl}/elearning-wp-remote/${contactId}/data-protection-decline`,
        body
      ),

    elearningAccept: (
      friendlyUrl: string,
      contactId: string,
      body: unknown
    ): Promise<AxiosResponse> =>
      apiClient.post(
        `/ehs-visitor-management/${friendlyUrl}/elearning-wp-remote/${contactId}/elearning-accept`,
        body
      ),

    finish: (friendlyUrl: string): Promise<AxiosResponse> =>
      apiClient.post(
        `/ehs-visitor-management/${friendlyUrl}/elearning-wp-remote/finish`
      ),

    getElearningTest: (
      friendlyUrl: string,
      contactId: string
    ): Promise<AxiosResponse> =>
      apiClient.get(
        `/ehs-visitor-management/${friendlyUrl}/elearning-wp-remote/${contactId}/elearning-test`
      ),

    submitElearningTest: (
      friendlyUrl: string,
      contactId: string,
      body: unknown
    ): Promise<AxiosResponse> =>
      apiClient.post(
        `/ehs-visitor-management/${friendlyUrl}/elearning-wp-remote/${contactId}/elearning-test`,
        body
      )
  },

  externalElearning: {
    login: (
      friendlyUrl: string,
      body: { token: string }
    ): Promise<AxiosResponse> =>
      apiClient.post(
        `/ehs-visitor-management/${friendlyUrl}/elearning-external/login`,
        body
      ),

    generateOtp: (friendlyUrl: string): Promise<AxiosResponse> =>
      apiClient.post(
        `/ehs-visitor-management/${friendlyUrl}/elearning-external/generate-otp`
      ),

    verifyOtp: (
      friendlyUrl: string,
      body: { otp: string }
    ): Promise<AxiosResponse> =>
      apiClient.post(
        `/ehs-visitor-management/${friendlyUrl}/elearning-external/otp`,
        body
      ),

    getPersonInfo: (friendlyUrl: string): Promise<AxiosResponse> =>
      apiClient.get(
        `/ehs-visitor-management/${friendlyUrl}/elearning-external`
      ),

    updatePerson: (
      friendlyUrl: string,
      contactId: string,
      body: unknown
    ): Promise<AxiosResponse> =>
      apiClient.post(
        `/ehs-visitor-management/${friendlyUrl}/elearning-external/${contactId}/update`,
        body
      ),

    dataProtectionDecline: (
      friendlyUrl: string,
      contactId: string,
      body: unknown
    ): Promise<AxiosResponse> =>
      apiClient.post(
        `/ehs-visitor-management/${friendlyUrl}/elearning-external/${contactId}/data-protection-decline`,
        body
      ),

    elearningAccept: (
      friendlyUrl: string,
      contactId: string,
      body: unknown
    ): Promise<AxiosResponse> =>
      apiClient.post(
        `/ehs-visitor-management/${friendlyUrl}/elearning-external/${contactId}/elearning-accept`,
        body
      ),

    finish: (friendlyUrl: string): Promise<AxiosResponse> =>
      apiClient.post(
        `/ehs-visitor-management/${friendlyUrl}/elearning-external/finish`
      ),

    getElearningTest: (
      friendlyUrl: string,
      contactId: string
    ): Promise<AxiosResponse> =>
      apiClient.get(
        `/ehs-visitor-management/${friendlyUrl}/elearning-external/${contactId}/elearning-test`
      ),

    submitElearningTest: (
      friendlyUrl: string,
      contactId: string,
      body: unknown
    ): Promise<AxiosResponse> =>
      apiClient.post(
        `/ehs-visitor-management/${friendlyUrl}/elearning-external/${contactId}/elearning-test`,
        body
      )
  },

  elearningWpOnSite: {
    login: (
      friendlyUrl: string,
      body: { username: string; password: string }
    ): Promise<AxiosResponse> =>
      apiClient.post(
        `/ehs-visitor-management/${friendlyUrl}/elearning-wp-onsite/login`,
        body
      ),

    mveNumber: (
      friendlyUrl: string,
      body: { mveNumber: string }
    ): Promise<AxiosResponse> =>
      apiClient.post(
        `/ehs-visitor-management/${friendlyUrl}/elearning-wp-onsite/mve-number`,
        body
      ),

    birthDate: (
      friendlyUrl: string,
      body: { birthDate: string }
    ): Promise<AxiosResponse> =>
      apiClient.post(
        `/ehs-visitor-management/${friendlyUrl}/elearning-wp-onsite/birth-date`,
        body
      ),

    getPersonInfo: (friendlyUrl: string): Promise<AxiosResponse> =>
      apiClient.get(
        `/ehs-visitor-management/${friendlyUrl}/elearning-wp-onsite`
      ),

    updatePerson: (
      friendlyUrl: string,
      contactId: string,
      body: unknown
    ): Promise<AxiosResponse> =>
      apiClient.post(
        `/ehs-visitor-management/${friendlyUrl}/elearning-wp-onsite/${contactId}/update`,
        body
      ),

    dataProtectionDecline: (
      friendlyUrl: string,
      contactId: string,
      body: unknown
    ): Promise<AxiosResponse> =>
      apiClient.post(
        `/ehs-visitor-management/${friendlyUrl}/elearning-wp-onsite/${contactId}/data-protection-decline`,
        body
      ),

    elearningAccept: (
      friendlyUrl: string,
      contactId: string,
      body: unknown
    ): Promise<AxiosResponse> =>
      apiClient.post(
        `/ehs-visitor-management/${friendlyUrl}/elearning-wp-onsite/${contactId}/elearning-accept`,
        body
      ),

    finish: (friendlyUrl: string): Promise<AxiosResponse> =>
      apiClient.post(
        `/ehs-visitor-management/${friendlyUrl}/elearning-wp-onsite/finish`
      ),

    getElearningTest: (
      friendlyUrl: string,
      contactId: string
    ): Promise<AxiosResponse> =>
      apiClient.get(
        `/ehs-visitor-management/${friendlyUrl}/elearning-wp-onsite/${contactId}/elearning-test`
      ),

    submitElearningTest: (
      friendlyUrl: string,
      contactId: string,
      body: unknown
    ): Promise<AxiosResponse> =>
      apiClient.post(
        `/ehs-visitor-management/${friendlyUrl}/elearning-wp-onsite/${contactId}/elearning-test`,
        body
      )
  }
};
